/**
 * Pure utility functions for converting a freehand brush stroke into a closed
 * outline polygon that can be stored as an annotation.
 *
 * None of these functions have side effects or DOM dependencies — they operate
 * only on plain data and an offscreen canvas element.
 */

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Converts a raw brush stroke (center-line points + brush radius) into a
 * simplified closed outline polygon.
 *
 * Algorithm:
 *  1. Build a tight bounding box around the stroke with padding.
 *  2. Render the stroke onto a small offscreen canvas.
 *  3. Extract a binary mask from the canvas alpha channel.
 *  4. Trace the outer contour with Moore Neighborhood Tracing.
 *  5. Simplify the contour with Douglas-Peucker.
 *
 * @param pts        Center-line points of the brush stroke (canvas coordinates).
 * @param halfWidth  Brush radius in canvas natural pixels.
 * @returns          Simplified outline polygon in canvas coordinates.
 */
export function computeBrushOutline(
  pts: { x: number; y: number }[],
  halfWidth: number
): { x: number; y: number }[] {
  // ── 1. Bounding box + padding ────────────────────────────────────────────
  const pad = halfWidth + 2;
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x - pad);  maxX = Math.max(maxX, p.x + pad);
    minY = Math.min(minY, p.y - pad);  maxY = Math.max(maxY, p.y + pad);
  }
  const bw = maxX - minX || 1;
  const bh = maxY - minY || 1;

  // ── 2. Offscreen canvas (~300 px on longest side for performance) ────────
  const SCALE    = Math.min(1, 300 / Math.max(bw, bh));
  const scaledHW = Math.max(2, halfWidth * SCALE);
  const cw = Math.max(4, Math.ceil(bw * SCALE));
  const ch = Math.max(4, Math.ceil(bh * SCALE));

  const off  = document.createElement('canvas');
  off.width  = cw;
  off.height = ch;
  const octx = off.getContext('2d')!;
  octx.fillStyle = octx.strokeStyle = '#000';
  octx.lineCap   = 'round';
  octx.lineJoin  = 'round';
  octx.lineWidth = scaledHW * 2;

  if (pts.length === 1) {
    octx.beginPath();
    octx.arc((pts[0].x - minX) * SCALE, (pts[0].y - minY) * SCALE, scaledHW, 0, Math.PI * 2);
    octx.fill();
  } else {
    octx.beginPath();
    octx.moveTo((pts[0].x - minX) * SCALE, (pts[0].y - minY) * SCALE);
    for (let i = 1; i < pts.length; i++) {
      octx.lineTo((pts[i].x - minX) * SCALE, (pts[i].y - minY) * SCALE);
    }
    octx.stroke();
  }

  // ── 3. Binary mask from alpha channel ────────────────────────────────────
  const imgData = octx.getImageData(0, 0, cw, ch).data;
  const mask = new Uint8Array(cw * ch);
  for (let i = 0; i < cw * ch; i++) {
    mask[i] = imgData[i * 4 + 3] > 64 ? 1 : 0;
  }

  // ── 4. Moore Neighborhood contour trace ──────────────────────────────────
  const contourPx = traceOuterContour(mask, cw, ch);
  if (contourPx.length < 3) {
    // Fallback: return the bounding box corners
    return [
      { x: minX, y: minY }, { x: maxX, y: minY },
      { x: maxX, y: maxY }, { x: minX, y: maxY },
    ];
  }

  // ── 5. Convert to canvas coordinates + Douglas-Peucker simplification ────
  const rawPts = contourPx.map(([px, py]) => ({
    x: px / SCALE + minX,
    y: py / SCALE + minY,
  }));
  return douglasPeuckerSimplify(rawPts, (1 / SCALE) * 1.5);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Moore Neighborhood Tracing — returns the outer contour pixels of a binary mask.
 * Traverses 8-connected neighbours clockwise starting from the East direction.
 */
function traceOuterContour(mask: Uint8Array, w: number, h: number): [number, number][] {
  const NDX = [ 1,  1,  0, -1, -1, -1,  0,  1];
  const NDY = [ 0,  1,  1,  1,  0, -1, -1, -1];

  // Find the topmost-leftmost foreground pixel as the starting point
  let sx = -1, sy = -1;
  for (let y = 0; y < h && sx < 0; y++) {
    for (let x = 0; x < w && sx < 0; x++) {
      if (mask[y * w + x]) { sx = x; sy = y; }
    }
  }
  if (sx < 0) return [];

  const result: [number, number][] = [];
  let cx = sx, cy = sy;
  // Background pixel west of the start — guaranteed to exist since sx is leftmost in its row
  let bx = sx - 1, by = sy;
  const MAX = (w + h) * 4 + 8;

  for (let iter = 0; iter <= MAX; iter++) {
    result.push([cx, cy]);

    // Find direction index from current pixel → last background pixel
    let enterDir = -1;
    for (let d = 0; d < 8; d++) {
      if (cx + NDX[d] === bx && cy + NDY[d] === by) { enterDir = d; break; }
    }
    if (enterDir < 0) break;

    // Search clockwise from enterDir + 1; track the last in-bounds background pixel as new b
    let newBx = bx, newBy = by;
    let nx = -1, ny = -1;
    for (let i = 1; i <= 8; i++) {
      const d  = (enterDir + i) % 8;
      const px = cx + NDX[d], py = cy + NDY[d];
      if (px >= 0 && px < w && py >= 0 && py < h) {
        if (mask[py * w + px]) { nx = px; ny = py; break; }
        else { newBx = px; newBy = py; }
      }
    }
    if (nx < 0) break;
    bx = newBx; by = newBy;
    cx = nx;    cy = ny;
    if (cx === sx && cy === sy) break;  // Back at start — contour is closed
  }
  return result;
}

/**
 * Douglas-Peucker polyline simplification.
 * Recursively removes points that are within `eps` distance from the line
 * connecting the first and last point of each segment.
 */
function douglasPeuckerSimplify(
  pts: { x: number; y: number }[],
  eps: number
): { x: number; y: number }[] {
  if (pts.length <= 2) return pts;

  const first = pts[0], last = pts[pts.length - 1];
  const dx = last.x - first.x, dy = last.y - first.y;
  const len = Math.hypot(dx, dy);

  let maxDist = 0, maxIdx = 1;
  for (let i = 1; i < pts.length - 1; i++) {
    const d = len > 0
      ? Math.abs(dy * pts[i].x - dx * pts[i].y + last.x * first.y - last.y * first.x) / len
      : Math.hypot(pts[i].x - first.x, pts[i].y - first.y);
    if (d > maxDist) { maxDist = d; maxIdx = i; }
  }

  if (maxDist > eps) {
    const left  = douglasPeuckerSimplify(pts.slice(0, maxIdx + 1), eps);
    const right = douglasPeuckerSimplify(pts.slice(maxIdx), eps);
    return [...left.slice(0, -1), ...right];
  }
  return [first, last];
}
